import { inject, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { LegacySidebarService } from 'guideline/feature-sidebar';
import { cloneDeep } from 'lodash-es';
import { Subject } from 'rxjs';
import { MedicalComponentService, ComponentConverter, SelectionService } from 'shared/domain';

import {
  ActivityDefinitionDto,
  ComponentDto,
  ComponentLinkType,
  FlowchartElementType,
  FlowchartNodeDto,
  ProposedComponentDto,
} from '@treetop/shared/model';

import { FlowchartEventsService } from './flowchart-events.service';
import { FlowchartConfig } from './flowchart.config';
import { FlowchartSidebarStoreService } from '../../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import { FlowchartNode } from '../../entities/FlowchartNode';
import { FlowchartElementsSubjectStore } from '../data/flowchart-elements-subject-store.service';
import { BaseGraph } from '../flowchart/graph/BaseGraph';
import { FlowchartModelService } from '../flowchart/model.service';
import { BasePaper } from '../flowchart/paper/BasePaper';
import { FlowchartViewService } from '../flowchart/view.service';
import { FlowchartLoaderService } from '../operations/flowchart-loader.service';
import { SizeService } from '../operations/size.service';

@Injectable()
export class FlowchartSearchService {
  private flowchartViewService = inject(FlowchartViewService);
  flowchartModelService = inject(FlowchartModelService);
  flowchartSidebarStoreService = inject(FlowchartSidebarStoreService);
  private sidebarService = inject(LegacySidebarService);
  private componentService = inject(MedicalComponentService);
  private translocoService = inject(TranslocoService);
  private componentConverter = inject(ComponentConverter);
  private flowchartLoaderService = inject(FlowchartLoaderService);
  private selectionService = inject(SelectionService);

  private flowchartElementsSubjectStore = inject(FlowchartElementsSubjectStore);

  toolbar: joint.ui.Toolbar;
  flowchartElement: FlowchartNodeDto;
  findingFilterActive = true;
  activityFilterActive = true;
  observationFilterActive = false;
  private sizeService: SizeService;
  private componentAdded = new Subject<void>();
  paperSource: BasePaper;
  graphSource: BaseGraph;

  private flowchartId: string;

  constructor() {
    this.sizeService = new SizeService();
    this.paperSource = this.flowchartViewService.getPaper();
    this.graphSource = this.flowchartModelService.getGraph();
  }

  public init(flowchartId: string) {
    this.flowchartId = flowchartId;
  }

  handleSearchSelection(component: FormControl, selectedElementView: joint.dia.ElementView) {
    if (selectedElementView.model.attributes.isContainer || selectedElementView.model.attributes.isSubflow) {
      const embeds = selectedElementView.model.attributes.embeds;
      if (embeds) {
        const embeddedHeader = this.graphSource.graph.getCell(embeds[0]);
        this._setNodeText(embeddedHeader, component, true);
      }
    } else {
      const node = this.graphSource.graph.getCell(selectedElementView.model.id);
      this._setNodeText(node, component, false);
    }
  }

  _setNodeText(node: joint.dia.Cell, component: FormControl, embeds: boolean) {
    const nodePrevHeight = node.attributes.size.height;
    const nodePrevWidth = node.attributes.size.width;
    if (component.value.title && node) {
      const nodeTitle = node.attr('label/text');
      node.attr('label/text', component.value.title);

      if (!embeds) {
        const nodeSize = this.sizeService.setNodeSize(component.value.title.length);
        if (nodeTitle === this.translocoService.translate(FlowchartConfig.defaultTitleKey)) {
          node.prop('size', nodeSize);
          // TODO: Refactor this later and move it to a service after setting the subject!
          if (component.value.type !== 0) {
            this.centerNodeAfterResize(node, nodeSize.width, nodeSize.height, nodePrevWidth, nodePrevHeight);
            this.flowchartLoaderService.setElementPositionInSubject(
              this.flowchartElementsSubjectStore,
              this.flowchartViewService.getPaper().paper.findViewByModel(node),
            );
          }
        }
      }
    } else if (component.value.ruleTitle) {
      const ruleTitle = new DOMParser().parseFromString(component.value.ruleTitle, 'text/html').documentElement.textContent;
      node.attr('label/text', ruleTitle);
    }
  }

  private centerNodeAfterResize(node: joint.dia.Cell, newWidth: number, newHeight: number, prevWidth: number, prevHeight: number) {
    let posXDiff = 0;
    let posYDiff = 0;
    if (prevWidth < newWidth) {
      posXDiff = newWidth - prevWidth;
    }

    if (prevHeight < newHeight) {
      posYDiff = newHeight - prevHeight;
    }

    if (posXDiff !== 0 || posYDiff !== 0) {
      node.prop('position', { x: node.attributes.position.x - posXDiff / 2, y: node.attributes.position.y - posYDiff / 2 });
    }
  }

  assignConvertedMedicalComponentsToSubject(
    definition: ComponentDto | ProposedComponentDto,
    selectedElementView: joint.dia.ElementView,
    flowchartEventsService: FlowchartEventsService,
  ): void {
    let medicationRequests: ActivityDefinitionDto[] = [];
    let hasMedication = false;
    if (!definition.code.startsWith('PC')) {
      if ((definition as ComponentDto)?.componentLinks !== null) {
        const medicationGroupLinks = (definition as ComponentDto).componentLinks?.filter(
          (l) => l.linkType === ComponentLinkType.MedicationGroup,
        );
        if (medicationGroupLinks?.length > 0) {
          hasMedication = true;
          // Remove event handlers to prevent interacting with flowchart while loading medication requests
          flowchartEventsService.removeEventHandlers();
          this.paperSource.paper.options.interactive = {
            labelMove: false,
            linkMove: false,
            elementMove: false,
          };
          this.paperSource.paper.freeze();

          this.componentService.loadMedicationRequests(definition.code, (definition as ComponentDto).version).subscribe(
            (result) => {
              medicationRequests = [...result];
              const dtoElement = this.sidebarService.deepSearchElements(
                this.flowchartElementsSubjectStore.getValue(),
                selectedElementView.model.attributes.id,
              );

              this.handleAssignConvertedMedicalComponentToSubject(dtoElement, definition, medicationRequests, selectedElementView);
              setTimeout(() => {
                flowchartEventsService.initializeEventHandlers({
                  allowEvents: true,
                });
                this.paperSource.paper.options.interactive = {
                  labelMove: true,
                  linkMove: false,
                  elementMove: true,
                };
                this.paperSource.paper.unfreeze();
              }, 200);
            },
            (error) => {
              console.error(error);
              flowchartEventsService.initializeEventHandlers();
            },
          );
        }
      }
    }

    if (!hasMedication) {
      const dtoElement = this.sidebarService.deepSearchElements(
        this.flowchartElementsSubjectStore.getValue(),
        selectedElementView.model.attributes.id,
      );

      this.handleAssignConvertedMedicalComponentToSubject(dtoElement, definition, medicationRequests, selectedElementView);
    }
  }

  private async handleAssignConvertedMedicalComponentToSubject(
    dtoElement: FlowchartNodeDto,
    definition: ComponentDto | ProposedComponentDto,
    medicationRequests: ActivityDefinitionDto[],
    selectedElementView: joint.dia.ElementView,
  ) {
    const flowchartNode = new FlowchartNode(cloneDeep(dtoElement));

    await flowchartNode.setPropertiesAfterSearch(
      definition,
      medicationRequests,
      this.componentConverter,
      null,
      null,
      this.flowchartSidebarStoreService,
    );

    if (selectedElementView.model.attributes.subflowParentId) {
      this.sizeService.resizeParentToFit(
        this.graphSource.graph.getCell(selectedElementView.model.attributes.subflowParentId as string) as joint.dia.Element,
        this.graphSource.graph,
      );
    }
    if (flowchartNode.getElement().elementType === FlowchartElementType.FlowchartStartNode) {
      this.sidebarService.updateCurrentElement(this.flowchartId, dtoElement.id, this.flowchartElementsSubjectStore.getValue(), false);
    }

    this.componentAdded.next();
    this.selectionService.selectFlowchartElement(this.flowchartId, dtoElement.uniqueId);
  }

  adjustFilters(elementView: joint.dia.CellView) {
    const flowchartElement = this.flowchartElementsSubjectStore
      .getValue()
      .find((element) => element.id === elementView.model.attributes.id);
    if (flowchartElement) {
      this.flowchartElement = flowchartElement as FlowchartNodeDto;
      switch (flowchartElement.elementType) {
        case FlowchartElementType.FlowchartStartNode:
          this.setFiltersByBoolean(true, true, false);
          break;
        case FlowchartElementType.FlowchartAction:
          this.setFiltersByBoolean(false, false, false);
          break;
        case FlowchartElementType.FlowchartDecisionNode:
          this.setFiltersByBoolean(true, false, true);
          break;
        default:
          this.setFiltersByBoolean(true, true, false);
          break;
      }
    }
  }

  setFiltersByBoolean(findingFilterActive: boolean, activityFilterActive: boolean, observationFilterActive: boolean) {
    this.findingFilterActive = findingFilterActive;
    this.activityFilterActive = activityFilterActive;
    this.observationFilterActive = observationFilterActive;
  }

  getComponentAddedObservable() {
    return this.componentAdded.asObservable();
  }
}
